<script lang="ts">
  import { Input } from "@/lib/components/ui/input";
  import { Separator } from "@/lib/components/ui/separator";
  import * as Select from "$lib/components/ui/select";
  import Button from "@/lib/components/ui/button/button.svelte";
  import { difficultyOptions, focusOptions, sortDirOptions, sortOptions, specificityOptions, trainingTypeOptions, type SearchFilterFieldsAndData } from "./SearchFilterFieldsAndData";
  import Switch from "@/lib/components/ui/switch/switch.svelte";
  import * as Collapsible from "@/lib/components/ui/collapsible";
  import { buttonVariants } from "@/lib/components/ui/button";
  import { cn } from "@/lib/utils";
  import { Checkbox } from "@/lib/components/ui/checkbox";
  import { Label } from "@/lib/components/ui/label";

  export let fields: SearchFilterFieldsAndData;

  export let includeTrainingTypeFilters: boolean = false;
  export let includeWorkoutTypeSelector: boolean = true;
  export let hideResetButton: boolean = false;

  function resetFilters() {
    if (!fields) return;
    fields.resetFilters();
    // Force an assignment so Svelte propagates changes
    fields = fields;
  }

  let showMoreXSSFilters: boolean = false;

</script>

{#if fields}
  <div class='flex flex-col gap-2'>
    <div>
      <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Sort By</h3>
      <div class="grid grid-cols-2 gap-2">
        <Select.Root portal="#shadcn-ui-dialog-target" bind:selected={fields.sortBy} items={sortOptions} onSelectedChange={fields.onSortByChange}>
          <Select.Trigger class={cn(fields.isFilterApplied('sortBy') ? 'border-2 border-blue-400' : '')} >
            <Select.Value/>
          </Select.Trigger>
          <Select.Content class="overflow-auto max-h-48">
            {#each sortOptions as duration}
              <Select.Item value={duration.value}>{duration.label}</Select.Item>
            {/each}
          </Select.Content>
        </Select.Root>
        <Select.Root portal="#shadcn-ui-dialog-target" bind:selected={fields.sortDirection} items={sortDirOptions} onSelectedChange={fields.onSortDirectionChange}>
          <Select.Trigger class={cn(fields.isFilterApplied('sortDirection') ? 'border-2 border-blue-400' : '')} >
            <Select.Value />
          </Select.Trigger>
          <Select.Content class="overflow-auto max-h-48">
            {#each sortDirOptions as dir}
              <Select.Item value={dir.value}>{dir.label}</Select.Item>
            {/each}
          </Select.Content>
        </Select.Root>
      </div>
    </div>

    <Separator />

    <div>
        <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Search</h3>
        <Input id="selected-date" placeholder='Search...' class={cn(fields.isFilterApplied('searchTerm') ? 'border-2 border-blue-400' : '')}  bind:value={fields.searchTerm} on:input={fields.executeFilterDebouncer}/>
    </div>

    {#if includeTrainingTypeFilters}
      <Separator />

      <div class='flex flex-row flex-wrap gap-2 items-center'>
        {#each trainingTypeOptions as trainingType}
          {#if trainingType.value != 'sessions'}
            <Button variant={fields.trainingType.value === trainingType.value ? 'default' : 'outline'} size='sm' class='rounded-full' on:click={() => { fields.trainingType = trainingType; fields.onBooleanOrCategoricalFilterChange(); }}>
              {trainingType.label}
            </Button>
          {/if}
        {/each}
      </div>
    {/if}

    <Separator />

    <div>
      <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Duration (minutes)</h3>
      <div class="grid grid-cols-2 gap-2">
        <Input type="number" placeholder="Min" class={cn(fields.isFilterApplied('minDuration') ? 'border-2 border-blue-400' : '')} bind:value={fields.minDuration} on:input={fields.onNumericFilterChange} />
        <Input type="number" placeholder="Max" class={cn(fields.isFilterApplied('maxDuration') ? 'border-2 border-blue-400' : '')} bind:value={fields.maxDuration} on:input={fields.onNumericFilterChange} />
      </div>
    </div>

    <div>
      <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Difficulty</h3>
      <div class="grid grid-cols-2 gap-2">
        <Select.Root portal="#shadcn-ui-dialog-target" bind:selected={fields.minDifficulty} items={difficultyOptions} onSelectedChange={fields.onBooleanOrCategoricalFilterChange}>
          <Select.Trigger class={cn(fields.isFilterApplied('minDifficulty') ? 'border-2 border-blue-400' : '')} >
            <Select.Value />
          </Select.Trigger>
          <Select.Content class="overflow-auto max-h-48">
            {#each difficultyOptions as difficulty}
              <Select.Item value={difficulty.value}>{difficulty.label}</Select.Item>
            {/each}
          </Select.Content>
        </Select.Root>
        <Select.Root portal="#shadcn-ui-dialog-target" bind:selected={fields.maxDifficulty} items={difficultyOptions} onSelectedChange={fields.onBooleanOrCategoricalFilterChange}>
          <Select.Trigger class={cn(fields.isFilterApplied('maxDifficulty') ? 'border-2 border-blue-400' : '')} >
            <Select.Value />
          </Select.Trigger>
          <Select.Content class="overflow-auto max-h-48">
            {#each difficultyOptions as difficulty}
              <Select.Item value={difficulty.value}>{difficulty.label}</Select.Item>
            {/each}
          </Select.Content>
        </Select.Root>
      </div>
    </div>
    
    <div>
      <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">XSS</h3>
      <div class="grid grid-cols-2 gap-2">
        <Input type="number" placeholder="Min" class={cn(fields.isFilterApplied('minXss') ? 'border-2 border-blue-400' : '')} bind:value={fields.minXss} on:input={fields.onNumericFilterChange} />
        <Input type="number" placeholder="Max" class={cn(fields.isFilterApplied('maxXss') ? 'border-2 border-blue-400' : '')} bind:value={fields.maxXss} on:input={fields.onNumericFilterChange} />
      </div>
    </div>
    
    <Collapsible.Root bind:open={showMoreXSSFilters}>
      <Collapsible.Trigger
        class={buttonVariants({ variant: "ghost", size: 'sm', class: 'text-muted-foreground' })}
      >
        <span class='underline'>More XSS Filters</span>
        <i class={cn('fa-solid fa-fw ml-2', showMoreXSSFilters ? 'fa-chevron-up' : 'fa-chevron-down')} />
      </Collapsible.Trigger>
      <Collapsible.Content class='flex flex-col gap-2 mt-2'>
        <div>
          <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Low XSS</h3>
          <div class="grid grid-cols-2 gap-2">
            <Input type="number" placeholder="Min" class={cn(fields.isFilterApplied('minXlss') ? 'border-2 border-blue-400' : '')} bind:value={fields.minXlss} on:input={fields.onNumericFilterChange} />
            <Input type="number" placeholder="Max" class={cn(fields.isFilterApplied('maxXlss') ? 'border-2 border-blue-400' : '')} bind:value={fields.maxXlss} on:input={fields.onNumericFilterChange} />
          </div>
        </div>
        
        <div>
          <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">High XSS</h3>
          <div class="grid grid-cols-2 gap-2">
            <Input type="number" placeholder="Min" class={cn(fields.isFilterApplied('minXhss') ? 'border-2 border-blue-400' : '')} bind:value={fields.minXhss} on:input={fields.onNumericFilterChange} />
            <Input type="number" placeholder="Max" class={cn(fields.isFilterApplied('maxXhss') ? 'border-2 border-blue-400' : '')} bind:value={fields.maxXhss} on:input={fields.onNumericFilterChange} />
          </div>
        </div>
        
        <div>
          <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Peak XSS</h3>
          <div class="grid grid-cols-2 gap-2">
            <Input type="number" placeholder="Min" class={cn(fields.isFilterApplied('minXpss') ? 'border-2 border-blue-400' : '')} bind:value={fields.minXpss} on:input={fields.onNumericFilterChange} />
            <Input type="number" placeholder="Max" class={cn(fields.isFilterApplied('maxXpss') ? 'border-2 border-blue-400' : '')} bind:value={fields.maxXpss} on:input={fields.onNumericFilterChange} />
          </div>
        </div>
      </Collapsible.Content>
    </Collapsible.Root>

    <Separator />

    <div>
      <div class="grid grid-cols-2 gap-2">
        <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Specificity</h3>
        <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Focus</h3>
        <Select.Root portal="#shadcn-ui-dialog-target" bind:selected={fields.selectedSpecificity} items={specificityOptions} onSelectedChange={fields.onBooleanOrCategoricalFilterChange}>
          <Select.Trigger class={cn(fields.isFilterApplied('selectedSpecificity') ? 'border-2 border-blue-400' : '')} >
            <Select.Value />
          </Select.Trigger>
          <Select.Content class="overflow-auto max-h-48">
            {#each specificityOptions as specificity}
              <Select.Item value={specificity.value}>{specificity.label}</Select.Item>
            {/each}
          </Select.Content>
        </Select.Root>
        <Select.Root portal="#shadcn-ui-dialog-target" bind:selected={fields.selectedFocus} items={focusOptions} onSelectedChange={fields.onBooleanOrCategoricalFilterChange}>
          <Select.Trigger class={cn(fields.isFilterApplied('selectedFocus') ? 'border-2 border-blue-400' : '')} >
            <Select.Value />
          </Select.Trigger>
          <Select.Content class="overflow-auto max-h-48">
            {#each focusOptions as focus}
              <Select.Item value={focus.value}>{focus.label}</Select.Item>
            {/each}
          </Select.Content>
        </Select.Root>
      </div>
    </div>

    <Separator />

    <div class='flex flex-col gap-1'>
      <div class="grid grid-cols-2 gap-2">
        <div class='flex flex-row gap-2 items-center my-2'>
          <Switch bind:checked={fields.isLiked} on:click={fields.onBooleanOrCategoricalFilterChange} />
          <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Liked</h3>
        </div>
        <div class='flex flex-row gap-2 items-center my-2'>
          <Switch bind:checked={fields.isDisliked} on:click={fields.onBooleanOrCategoricalFilterChange} />
          <h3 class="text-sm font-medium tracking-wide text-muted-foreground m-0">Disliked</h3>
        </div>
      </div>
      {#if includeWorkoutTypeSelector}
      <div class="flex flex-row items-center gap-3">
        <Checkbox id="standard" class='border-border p-0' bind:checked={fields.showStandard} on:click={fields.onBooleanOrCategoricalFilterChange} />
        <Label class='text-sm font-medium tracking-wide text-muted-foreground m-0' on:click={fields.onBooleanOrCategoricalFilterChange} for="standard">Standard</Label>
      </div>
      <div class="flex flex-row items-center gap-3">
        <Checkbox id="community" class='border-border p-0' bind:checked={fields.showCommunity} on:click={fields.onBooleanOrCategoricalFilterChange} />
        <Label class='text-sm font-medium tracking-wide text-muted-foreground m-0' on:click={fields.onBooleanOrCategoricalFilterChange} for="community">Community</Label>
      </div>
      <div class="flex flex-row items-center gap-3">
        <Checkbox id="personal" class='border-border p-0' bind:checked={fields.showPersonal} on:click={fields.onBooleanOrCategoricalFilterChange} />
        <Label class='text-sm font-medium tracking-wide text-muted-foreground m-0' on:click={fields.onBooleanOrCategoricalFilterChange} for="personal">Personal</Label>
      </div>
      {/if}
    </div>

    <Separator />

    {#if !hideResetButton}
      <Button variant='outline' class='w-full' on:click={resetFilters}>
        Reset
      </Button>
    {/if}
  </div>
{/if}
